import { rtkAPI } from 'rtk/rtkSetup';
import api from 'util/api';
import { serializeRtkQueryError } from 'util/rtkQueryError';
import { loadBillingAuditLabels } from 'util/organizationBillingAuditDisplay';
import { loadAllHostedAccounts } from 'util/organizationHostedAccounts';

const tag = 'OrganizationBilling';
const enhancedApi = rtkAPI.enhanceEndpoints({ addTagTypes: [tag] });

const wrap = operation => async () => {
  try {
    return { data: await operation() };
  } catch (error) {
    return { error: serializeRtkQueryError(error) };
  }
};

export const organizationBillingApi = enhancedApi.injectEndpoints({
  endpoints: build => ({
    getOrganizationBillingDashboard: build.query({
      queryFn: wrap(() => api.OrganizationBilling.dashboard()),
      providesTags: [tag]
    }),
    getOrganizationBillingCycles: build.query({
      queryFn: args => wrap(() => api.OrganizationBilling.cycles(args))(),
      providesTags: [tag]
    }),
    getOrganizationBillingAudits: build.query({
      queryFn: args => wrap(() => api.OrganizationBilling.auditEvents(args))(),
      providesTags: [tag]
    }),
    getOrganizationBillingAuditLabels: build.query({
      queryFn: events => wrap(() => loadBillingAuditLabels(events))(),
      providesTags: [tag]
    }),
    getOrganizationBillingProfile: build.query({
      queryFn: wrap(() => api.OrganizationBilling.profile()),
      providesTags: [tag]
    }),
    getOrganizationHostedAccounts: build.query({
      async queryFn(args) {
        const {
          indexStart = 0,
          maxResults = 10,
          search = '',
          allPages = false
        } = args || {};
        try {
          return {
            data: allPages
              ? await loadAllHostedAccounts()
              : await api.OrganizationBilling.hostedAccounts(
                  indexStart,
                  maxResults,
                  search
                )
          };
        } catch (error) {
          return { error: serializeRtkQueryError(error) };
        }
      },
      providesTags: [tag]
    }),
    previewOrganizationHostedDeletion: build.mutation({
      queryFn: guid =>
        wrap(() => api.OrganizationBilling.hostedDeletionPreview(guid))()
    }),
    requestOrganizationHostedDeletion: build.mutation({
      async onQueryStarted({ guid }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const deletion = data?.Data ?? data?.data ?? data;
          const queries = organizationBillingApi.util.selectInvalidatedBy(
            getState(),
            [tag]
          );
          queries
            .filter(
              query => query.endpointName === 'getOrganizationHostedAccounts'
            )
            .forEach(query => {
              dispatch(
                organizationBillingApi.util.updateQueryData(
                  query.endpointName,
                  query.originalArgs,
                  cached => {
                    const page = cached?.Data ?? cached?.data ?? cached;
                    const account = (page?.Objects ?? page?.objects ?? []).find(
                      item => (item.GUID ?? item.guid) === guid
                    );
                    if (account) {
                      account.State = 'DeletionPending';
                      account.DeletionEffectiveUtc =
                        deletion?.EffectiveUtc ?? deletion?.effectiveUtc;
                    }
                  }
                )
              );
            });
        } catch {
          // Failed deletion requests leave the cached account unchanged.
        }
      },
      queryFn: ({ guid, idempotencyKey }) =>
        wrap(() =>
          api.OrganizationBilling.requestHostedDeletion(guid, idempotencyKey)
        )(),
      invalidatesTags: [tag]
    }),
    getOrganizationPricingPreview: build.query({
      queryFn: wrap(() => api.OrganizationBilling.pricingPreview()),
      providesTags: [tag]
    }),
    getOrganizationSponsorships: build.query({
      queryFn: ({
        indexStart = 0,
        maxResults = 100,
        search = '',
        historyOnly = false
      } = {}) =>
        wrap(() =>
          api.OrganizationBilling.sponsorships(
            indexStart,
            maxResults,
            search,
            historyOnly
          )
        )(),
      providesTags: [tag]
    }),
    getOrganizationSponsorshipCandidates: build.query({
      queryFn: ({ indexStart = 0, maxResults = 100, search = '' } = {}) =>
        wrap(() =>
          api.OrganizationBilling.sponsorshipCandidates(
            indexStart,
            maxResults,
            search
          )
        )(),
      providesTags: [tag]
    }),
    previewOrganizationSponsorship: build.mutation({
      queryFn: ({ notaryUserMasterGUID, frequency }) =>
        wrap(() =>
          api.OrganizationBilling.sponsorshipPreview(
            notaryUserMasterGUID,
            frequency
          )
        )()
    }),
    startOrganizationSponsorship: build.mutation({
      queryFn: ({ notaryUserMasterGUID, frequency, idempotencyKey }) =>
        wrap(() =>
          api.OrganizationBilling.startSponsorship(
            notaryUserMasterGUID,
            frequency,
            idempotencyKey
          )
        )(),
      invalidatesTags: [tag]
    }),
    previewOrganizationGroupSponsorship: build.mutation({
      queryFn: ({ notaryUserMasterGUIDs, frequency }) =>
        wrap(() =>
          api.OrganizationBilling.groupSponsorshipPreview(
            notaryUserMasterGUIDs,
            frequency
          )
        )()
    }),
    startOrganizationGroupSponsorship: build.mutation({
      queryFn: ({ notaryUserMasterGUIDs, frequency, idempotencyKey }) =>
        wrap(() =>
          api.OrganizationBilling.startGroupSponsorship(
            notaryUserMasterGUIDs,
            frequency,
            idempotencyKey
          )
        )(),
      invalidatesTags: [tag]
    }),
    endOrganizationSponsorship: build.mutation({
      queryFn: ({ guid, idempotencyKey }) =>
        wrap(() =>
          api.OrganizationBilling.endSponsorship(guid, idempotencyKey)
        )(),
      invalidatesTags: [tag]
    }),
    getOrganizationPaymentMethods: build.query({
      queryFn: wrap(() => api.OrganizationBilling.paymentMethods()),
      providesTags: [tag]
    }),
    createOrganizationSetupIntent: build.mutation({
      queryFn: idempotencyKey =>
        wrap(() => api.OrganizationBilling.createSetupIntent(idempotencyKey))()
    }),
    confirmOrganizationPaymentMethod: build.mutation({
      queryFn: setupIntentId =>
        wrap(() =>
          api.OrganizationBilling.confirmPaymentMethod(setupIntentId)
        )(),
      invalidatesTags: [tag]
    }),
    setDefaultOrganizationPaymentMethod: build.mutation({
      queryFn: ({ guid, idempotencyKey }) =>
        wrap(() => api.OrganizationBilling.setDefault(guid, idempotencyKey))(),
      invalidatesTags: [tag]
    }),
    setOrganizationPaymentMethodRole: build.mutation({
      queryFn: ({ guid, idempotencyKey, isDefault, isBackup }) =>
        wrap(() =>
          api.OrganizationBilling.setRole(guid, idempotencyKey, {
            IsDefault: isDefault,
            IsBackup: isBackup
          })
        )(),
      invalidatesTags: [tag]
    }),
    detachOrganizationPaymentMethod: build.mutation({
      queryFn: ({ guid, idempotencyKey }) =>
        wrap(() => api.OrganizationBilling.detach(guid, idempotencyKey))(),
      invalidatesTags: [tag]
    }),
    getOrganizationInvoices: build.query({
      queryFn: args =>
        wrap(() =>
          api.OrganizationBilling.invoices(
            args?.indexStart ?? 0,
            args?.maxResults ?? 10,
            args?.filters ?? {}
          )
        )(),
      providesTags: [tag]
    }),
    getOrganizationInvoice: build.query({
      queryFn: guid => wrap(() => api.OrganizationBilling.invoice(guid))(),
      providesTags: [tag]
    }),
    payOrganizationInvoice: build.mutation({
      queryFn: ({ invoiceGuid, paymentMethodGuid, idempotencyKey }) =>
        wrap(() =>
          api.OrganizationBilling.payInvoice(
            invoiceGuid,
            paymentMethodGuid,
            idempotencyKey
          )
        )(),
      invalidatesTags: [tag]
    })
  })
});

export const {
  useGetOrganizationBillingDashboardQuery,
  useGetOrganizationBillingCyclesQuery,
  useGetOrganizationBillingAuditsQuery,
  useGetOrganizationBillingAuditLabelsQuery,
  useGetOrganizationInvoicesQuery,
  useGetOrganizationInvoiceQuery,
  useGetOrganizationBillingProfileQuery,
  useGetOrganizationHostedAccountsQuery,
  usePreviewOrganizationHostedDeletionMutation,
  useRequestOrganizationHostedDeletionMutation,
  useGetOrganizationPricingPreviewQuery,
  useGetOrganizationSponsorshipsQuery,
  useGetOrganizationSponsorshipCandidatesQuery,
  usePreviewOrganizationSponsorshipMutation,
  useStartOrganizationSponsorshipMutation,
  usePreviewOrganizationGroupSponsorshipMutation,
  useStartOrganizationGroupSponsorshipMutation,
  useEndOrganizationSponsorshipMutation,
  useGetOrganizationPaymentMethodsQuery,
  useCreateOrganizationSetupIntentMutation,
  useConfirmOrganizationPaymentMethodMutation,
  useSetDefaultOrganizationPaymentMethodMutation,
  useSetOrganizationPaymentMethodRoleMutation,
  useDetachOrganizationPaymentMethodMutation,
  usePayOrganizationInvoiceMutation
} = organizationBillingApi;
